import React, { useState } from 'react';
import { useParams, Link, Navigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeft, ArrowRight, Sparkles, ZoomIn } from 'lucide-react';
import { CASE_STUDIES } from '../mock';
import { CASE_STUDY_DETAIL, CASE_META } from '../data/caseStudies';
import ClosingCTA from '../components/ClosingCTA';
import ClientLogo from '../components/ClientLogo';
import ProductMockup from '../components/ProductMockup';
import BrandCanvas, { hasBrandCanvas } from '../components/BrandCanvas';
import StrategyDiagram from '../components/StrategyDiagram';
import DiagramModal from '../components/DiagramModal';
import PulseCTA from '../components/PulseCTA';
import SEO from '../components/SEO';

const fadeUp = { initial: { opacity: 0, y: 24 }, whileInView: { opacity: 1, y: 0 }, viewport: { once: true, margin: '-40px' }, transition: { duration: 0.6, ease: [0.22, 1, 0.36, 1] } };

// A second mockup variant per case study, used as an in-line "proof visual" so
// the page reads as case-study heavy rather than DigitalWorks-heavy.
const proofMockup = {
  sephora:                 { second: 'superapp', kicker: 'LOYALTY · UAE' },
  'al-futtaim':            { second: 'optical',  kicker: 'CROSS-BRAND SERVICES' },
  magrabi:                 { second: 'beauty',   kicker: 'IN-STORE ORDER LOOKUP' },
  fenix:                   { second: 'superapp', kicker: 'B2B PORTAL · REGIONS' },
  'digitalworks-internal': { second: 'outdoor',  kicker: 'MULTI-WORKSTREAM VIEW' },
};

export default function CaseStudy() {
  const { slug } = useParams();
  const [diagramOpen, setDiagramOpen] = useState(false);
  const c = CASE_STUDIES.find((x) => x.slug === slug);
  const d = CASE_STUDY_DETAIL[slug];
  const media = CASE_META[slug] || {};
  const proof = proofMockup[slug] || { second: 'ledger', kicker: 'PROOF OF WORK' };
  if (!c || !d) return <Navigate to="/work" replace />;

  const brand = c.logoColor;
  // On-dark accent — every case study's `logoColor` (client's own brand) is
  // very dark and disappears on the near-black "What reached production" band.
  // We fall back to the site's brand green there so the outcome metric always
  // reads clearly, regardless of the client's colour.
  const brandOnDark = (() => {
    const hex = (brand || '').replace('#','');
    if (hex.length < 6) return '#7ED4A0';
    const r = parseInt(hex.slice(0,2),16);
    const g = parseInt(hex.slice(2,4),16);
    const b = parseInt(hex.slice(4,6),16);
    const lum = (0.299*r + 0.587*g + 0.114*b);
    return lum < 90 ? '#7ED4A0' : brand;
  })();

  return (
    <div data-testid={`case-study-${slug}`}>
      <SEO
        title={`${c.client.split(' · ')[0]} — ${d.category}`}
        description={c.problem}
        canonical={`https://digitalworks.co/work/${slug}`}
        ogImage={`/og-cards/work/${slug}.png`}
        type="article"
      />
      {/* HERO — brand-tinted */}
      <section className="pt-14 pb-12 relative overflow-hidden">
        <div
          aria-hidden
          className="pointer-events-none absolute -top-40 -right-40 w-[520px] h-[520px] rounded-full"
          style={{ background: `radial-gradient(closest-side, ${brand}18, transparent)` }}
        />
        <div
          aria-hidden
          className="pointer-events-none absolute -bottom-32 -left-32 w-[420px] h-[420px] rounded-full"
          style={{ background: 'radial-gradient(closest-side, rgba(34,169,79,0.10), transparent)' }}
        />
        <div className="dwx relative">
          <Link to="/work" className="inline-flex items-center gap-2 mono text-[11px] tracking-[0.24em] uppercase text-[#1A1A1A]/60 hover:text-[#1A1A1A]">
            <ArrowLeft size={14} /> All case studies
          </Link>
          <div className="mt-8 grid grid-cols-12 gap-8 items-end">
            <div className="col-span-12 lg:col-span-7">
              <motion.div {...fadeUp} className="mono text-[11px] tracking-[0.24em] uppercase" style={{ color: brand }}>{d.category}</motion.div>
              {/* Client logo shown at full display size on the detail page */}
              <motion.div {...fadeUp} transition={{ ...fadeUp.transition, delay: 0.06 }} className="mt-6">
                <ClientLogo logo={c.logo} name={c.client.split(' · ')[0]} size="lg" />
              </motion.div>
              <motion.h1 {...fadeUp} transition={{ ...fadeUp.transition, delay: 0.14 }} className="serif-tight text-[46px] md:text-[76px] leading-[0.96] mt-6">{d.heroKicker}</motion.h1>
              <motion.div {...fadeUp} transition={{ ...fadeUp.transition, delay: 0.24 }} className="mono text-[12px] uppercase tracking-widest text-[#1A1A1A]/55 mt-6">{c.sector} · {c.markets}</motion.div>
            </div>
            <motion.div {...fadeUp} transition={{ ...fadeUp.transition, delay: 0.3 }} className="col-span-12 lg:col-span-5">
              {/* Hero visual — real client photograph when available.
                  For cases with a BrandCanvas we skip the abstract ProductMockup entirely,
                  showing an editorial hero card instead (the delivered surface is proven later in Proof of work). */}
              <div
                className="rounded-[20px] overflow-hidden h-72 lg:h-80 bg-[#FBFAF6] border relative"
                style={{ borderColor: `${brand}22` }}
              >
                {media.heroImage ? (
                  <img src={media.heroImage} alt={`${c.client} — client context`} className="w-full h-full object-cover" loading="eager" />
                ) : hasBrandCanvas(slug) ? (
                  <div className="w-full h-full flex flex-col justify-between p-8" style={{ background: `linear-gradient(135deg, ${brand}0A, ${brand}18)` }}>
                    <div className="mono text-[10px] tracking-[0.24em] uppercase" style={{ color: brand }}>{d.tag}</div>
                    <div>
                      <div className="serif-tight text-[34px] leading-[1.02]" style={{ color: brand }}>{c.client.split(' · ')[0]}</div>
                      <div className="serif text-[16px] mt-2 text-[#1A1A1A]/75">{c.sector}</div>
                      <div className="mono text-[10px] tracking-[0.2em] uppercase mt-4 text-[#1A1A1A]/55">Delivered surface below ↓</div>
                    </div>
                  </div>
                ) : (
                  <ProductMockup variant={c.mockupVariant} kicker={d.category.toUpperCase()} />
                )}
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* CHALLENGE / SOLUTION — brand-tinted band, not flat cream */}
      <section className="py-16" style={{ background: `linear-gradient(180deg, ${brand}08 0%, #F4F2EC 100%)` }}>
        <div className="dwx grid grid-cols-12 gap-10">
          <motion.div {...fadeUp} className="col-span-12 md:col-span-6">
            <div className="mono text-[10px] tracking-[0.24em] uppercase text-[#C4633F] mb-3">The challenge</div>
            <p className="text-[17px] leading-[1.65] text-[#1A1A1A]/85">{d.challenges}</p>
          </motion.div>
          <motion.div {...fadeUp} transition={{ ...fadeUp.transition, delay: 0.12 }} className="col-span-12 md:col-span-6">
            <div className="mono text-[10px] tracking-[0.24em] uppercase text-[#178838] mb-3">The solution</div>
            <p className="text-[17px] leading-[1.65] text-[#1A1A1A]/85">{d.solution}</p>
          </motion.div>
        </div>
      </section>

      {/* PROOF OF WORK — brand-inspired product canvas for named clients */}
      <section className="py-16 bg-[#FBFAF6]">
        <div className="dwx">
          <div className="grid grid-cols-12 gap-10 items-end mb-8">
            <div className="col-span-12 md:col-span-6">
              <div className="mono text-[10px] tracking-[0.24em] uppercase" style={{ color: brand }}>Proof of work</div>
              <h2 className="serif-tight text-[32px] md:text-[44px] leading-[1.02] mt-3">What the customer actually experiences.</h2>
            </div>
            <div className="col-span-12 md:col-span-6">
              <p className="text-[14px] leading-relaxed text-[#1A1A1A]/70">
                {hasBrandCanvas(slug)
                  ? 'Reference imagery drawn against the same brand system the programme ships against — not a client screenshot, but a faithful reading of the surface.'
                  : media.gallery && media.gallery.length > 0
                    ? 'Real client contexts from the programme — in-store, in-field and in-flight.'
                    : 'Illustrative screens rendered on the same design tokens the programme ships against.'}
              </p>
            </div>
          </div>

          {hasBrandCanvas(slug) ? (
            <motion.div
              {...fadeUp}
              className="rounded-[20px] overflow-hidden border"
              style={{ borderColor: `${brand}20`, aspectRatio: '900 / 480' }}
            >
              <BrandCanvas slug={slug} />
            </motion.div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <motion.div {...fadeUp} className="rounded-[20px] overflow-hidden h-80 bg-[#FBFAF6] border" style={{ borderColor: `${brand}20` }}>
                {media.gallery && media.gallery[0] ? (
                  <img src={media.gallery[0]} alt={`${c.client} — proof 1`} className="w-full h-full object-cover" loading="lazy" />
                ) : (
                  <ProductMockup variant={c.mockupVariant} kicker={`${c.tag.toUpperCase()} · SCREEN 01`} />
                )}
              </motion.div>
              <motion.div {...fadeUp} transition={{ ...fadeUp.transition, delay: 0.1 }} className="rounded-[20px] overflow-hidden h-80 bg-[#FBFAF6] border" style={{ borderColor: `${brand}20` }}>
                {media.gallery && media.gallery[1] ? (
                  <img src={media.gallery[1]} alt={`${c.client} — proof 2`} className="w-full h-full object-cover" loading="lazy" />
                ) : (
                  <ProductMockup variant={proof.second} kicker={proof.kicker} accent={brand} />
                )}
              </motion.div>
            </div>
          )}
        </div>
      </section>

      {/* STRATEGY DIRECTION — case-specific illustrated diagram */}
      <section className="py-16 bg-[#FBFAF6] border-y border-[#E8E4D9]">
        <div className="dwx">
          <div className="grid grid-cols-12 gap-10 items-end mb-8">
            <div className="col-span-12 md:col-span-7">
              <div className="mono text-[10px] tracking-[0.24em] uppercase" style={{ color: brand }}>The direction we took</div>
              <h2 className="serif-tight text-[32px] md:text-[44px] leading-[1.02] mt-3">Where we pointed the work.</h2>
            </div>
            <div className="col-span-12 md:col-span-5">
              <p className="text-[14px] leading-relaxed text-[#1A1A1A]/70">
                {slug === 'sephora'
                  ? 'The strategy on one page — where it converges, where it stays plural.'
                  : 'A single diagram, drawn from the programme, showing how the strategy actually lines up — where it converges, where it stays plural.'}
              </p>
              {slug !== 'sephora' && (
                <button
                  type="button"
                  onClick={() => setDiagramOpen(true)}
                  data-testid="diagram-deepdive-trigger"
                  className="mt-4 inline-flex items-center gap-2 mono text-[10px] tracking-[0.22em] uppercase text-[#178838] hover:text-[#0F5D2C] transition-colors"
                >
                  <ZoomIn size={13} /> Click to deep-dive
                </button>
              )}
            </div>
          </div>
          {slug === 'sephora' ? (
            <motion.div
              {...fadeUp}
              className="relative w-full rounded-[20px] overflow-hidden bg-[#FBFAF6] border border-[#E8E4D9]"
              data-testid="strategy-cards-sephora"
            >
              <StrategyDiagram slug={slug} />
            </motion.div>
          ) : (
            <motion.button
              type="button"
              onClick={() => setDiagramOpen(true)}
              aria-label="Open diagram deep-dive with annotated hotspots"
              data-testid="diagram-open-button"
              {...fadeUp}
              className="group relative w-full rounded-[20px] overflow-hidden bg-[#FBFAF6] border border-[#E8E4D9] hover:border-[#178838]/40 hover:shadow-[0_20px_60px_-30px_rgba(23,136,56,0.35)] transition-all text-left"
            >
              <StrategyDiagram slug={slug} />
              {/* Hover reveal overlay — subtle "expand" chip */}
              <div className="pointer-events-none absolute top-4 right-4 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                <div className="inline-flex items-center gap-2 rounded-full bg-[#0A0A0A] text-[#FBFAF6] px-3 py-1.5 mono text-[10px] tracking-[0.2em] uppercase">
                  <ZoomIn size={12} /> Deep-dive
                </div>
              </div>
            </motion.button>
          )}
        </div>
      </section>

      {slug !== 'sephora' && (
        <DiagramModal
          open={diagramOpen}
          slug={slug}
          title={d.heroKicker}
          onClose={() => setDiagramOpen(false)}
        />
      )}

      {/* STRATEGY STEPS — brand-accented rails */}
      <section className="py-16 bg-[#F4F2EC] border-y border-[#E8E4D9]">
        <div className="dwx">
          <motion.h2 {...fadeUp} className="serif-tight text-[38px] md:text-[54px] leading-[1] max-w-3xl">How we shaped the work.</motion.h2>
          <div className="mt-12 space-y-4">
            {d.strategySteps.map((s, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: '-40px' }}
                transition={{ duration: 0.55, delay: i * 0.08, ease: [0.22, 1, 0.36, 1] }}
                className="grid grid-cols-12 gap-6 rounded-[20px] bg-[#FBFAF6] p-8 lift relative overflow-hidden"
                style={{ borderLeft: `3px solid ${brand}`, border: `1px solid ${brand}22`, borderLeftWidth: 3 }}
              >
                <div
                  aria-hidden
                  className="pointer-events-none absolute -top-14 -right-14 w-[220px] h-[220px] rounded-full"
                  style={{ background: `radial-gradient(closest-side, ${brand}0F, transparent)` }}
                />
                <div className="col-span-12 md:col-span-3 relative">
                  <div className="mono text-[10px] tracking-[0.24em] uppercase" style={{ color: brand }}>Step {String(i + 1).padStart(2, '0')}</div>
                  <div className="serif text-[28px] leading-tight mt-2">{s.h}</div>
                </div>
                <div className="col-span-12 md:col-span-9 text-[15px] leading-[1.7] text-[#1A1A1A]/80 relative">{s.body}</div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* PHOTO GALLERY suppressed — real photos are already displayed inline in the Proof of work section above. */}

      {/* OUTCOMES — near-black band with subtle brand-tinted glow and translucent cards */}
      <section className="py-24 text-[#FBFAF6] relative overflow-hidden" style={{ backgroundColor: '#0A0A0A' }}>
        <div
          aria-hidden
          className="pointer-events-none absolute inset-0"
          style={{ background: `radial-gradient(1200px 500px at 15% 30%, ${brandOnDark}18, transparent 70%)` }}
        />
        <div className="dwx relative">
          <motion.div {...fadeUp} className="mono text-[10px] tracking-[0.24em] uppercase" style={{ color: brandOnDark }}>Outcomes</motion.div>
          <motion.h2
            {...fadeUp}
            transition={{ ...fadeUp.transition, delay: 0.08 }}
            className="serif-tight text-[52px] md:text-[80px] leading-[0.98] mt-4 max-w-4xl"
          >
            What reached production.
          </motion.h2>
          <div className="mt-14 grid grid-cols-1 md:grid-cols-3 gap-5 md:gap-6">
            {d.outcomes.map((o, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: '-40px' }}
                transition={{ duration: 0.55, delay: i * 0.1, ease: [0.22, 1, 0.36, 1] }}
                className="rounded-[20px] p-10 md:p-12 relative overflow-hidden flex flex-col items-center text-center"
                style={{
                  backgroundColor: 'rgba(255,255,255,0.02)',
                  border: '1px solid rgba(255,255,255,0.06)',
                }}
              >
                {/* Centred top rule in on-dark accent */}
                <div
                  aria-hidden
                  className="absolute top-0 left-1/2 -translate-x-1/2 h-px w-16"
                  style={{ background: brandOnDark }}
                />
                <div
                  className="serif-tight text-[44px] md:text-[52px] leading-[1] tracking-tight"
                  style={{ color: brandOnDark }}
                >
                  {o.v}
                </div>
                <div className="mt-6 text-[14px] md:text-[15px] leading-[1.6] text-[#FBFAF6]/70 max-w-[26ch]">{o.l}</div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* AI-NATIVE EXTENSION */}
      <section className="py-16 bg-[#0F3D22] text-[#FBFAF6]">
        <div className="dwx grid grid-cols-12 gap-10 items-center">
          <motion.div {...fadeUp} className="col-span-12 md:col-span-8">
            <div className="inline-flex items-center gap-2 mono text-[10px] tracking-[0.24em] uppercase text-[#7ED4A0]">
              <Sparkles size={13} /> AI-native, today
            </div>
            <h3 className="serif-tight text-[32px] md:text-[46px] leading-[1.05] mt-4">{d.aiExtension.title}</h3>
            <p className="text-[15px] leading-[1.7] text-[#FBFAF6]/80 mt-5 max-w-2xl">{d.aiExtension.body}</p>
          </motion.div>
          <motion.div {...fadeUp} transition={{ ...fadeUp.transition, delay: 0.14 }} className="col-span-12 md:col-span-4 flex md:justify-end">
            <PulseCTA to="/contact" size="md" variant="dark" testId={`case-study-${slug}-book-session`}>
              Talk to us about yours
            </PulseCTA>
          </motion.div>
        </div>
      </section>

      {/* NEXT CASE */}
      <section className="py-14 bg-[#FBFAF6]">
        <div className="dwx">
          <div className="mono text-[10px] tracking-[0.24em] uppercase text-[#1A1A1A]/55">More client work</div>
          <div className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-4">
            {CASE_STUDIES.filter((x) => x.slug !== slug).slice(0, 3).map((other) => (
              <Link key={other.slug} to={`/work/${other.slug}`} className="group rounded-[20px] bg-[#F4F2EC] border border-[#E8E4D9] p-8 lift">
                <div className="mono text-[10px] tracking-[0.24em] uppercase text-[#22A94F]">{other.tag}</div>
                <div className="mt-6 h-16 flex items-center">
                  <ClientLogo logo={other.logo} name={other.client.split(' · ')[0]} size="sm" />
                </div>
                <div className="text-[11px] mono uppercase tracking-widest text-[#1A1A1A]/50 mt-2">{other.markets}</div>
                <div className="mt-6 inline-flex items-center gap-1.5 text-[13px] font-medium text-[#178838] group-hover:text-[#22A94F]">Read case study <ArrowRight size={14} /></div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      <ClosingCTA />
    </div>
  );
}
